/**
 * Help table of contents (E13-F01) — sticky left column: search box, the 13
 * numbered topics grouped as in the original guide, and a scroll-spy highlight.
 *
 * ── Why scroll-position spy and not IntersectionObserver.
 * The guide's sections differ wildly in height (a three-step login vs. the whole
 * document stage), so "which section is intersecting" is ambiguous — several are,
 * constantly. What a reader actually means by "where am I" is "the last heading I
 * scrolled past", which is a position question, so it is answered by position:
 * on each (rAF-throttled) scroll we take the LAST section whose top has crossed a
 * fixed line below the top of the scroll port. This also gives a stable answer
 * after a TOC click, where an observer would fire for every section swept through
 * by the smooth scroll.
 *
 * ── The scroll port is `<main>`, not the window.
 * `AppShell` moved the scroll out of the window and into the content column
 * (`min-h-0 flex-1 overflow-y-auto`), so both the spy and `position: sticky`
 * resolve against that element — found at runtime by walking up from this nav
 * rather than hardcoding the shell's DOM shape.
 *
 * ── Why a click "pins" the active id instead of trusting the spy immediately.
 * The position spy's "at the very bottom, snap to the last topic" fallback
 * (below) exists so a short trailing section is still reachable — but it fires
 * on ANY click whose target has little content after it (e.g. `inbox`, with
 * only the short `clients`/`tips` sections left below): `scrollIntoView` can't
 * physically scroll `inbox`'s heading all the way to the reading line because
 * there is no more room, the container lands at max scroll, and the fallback
 * then claims `tips` is active instead of the section just clicked. So a click
 * sets `activeId` immediately and holds it for a short settle window — long
 * enough for the smooth scroll to finish — before handing back to the
 * position-based spy for organic scrolling.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  HELP_GROUPS,
  HELP_LEAD_ID_BY_PAGE,
  topicsForPage,
  helpSectionElementId,
  type HelpPageId,
  type HelpSectionMeta,
} from './help-sections.js';
import { scrollToHelpSection } from './HelpPrimitives.js';
import { HelpSearch } from './HelpSearch.js';

/**
 * Distance below the top of the scroll port at which a section counts as "the
 * one you are reading". Comfortably below the sections' `scroll-mt-6`, so a
 * topic jumped to via the TOC is immediately the active one.
 */
const ACTIVE_LINE_OFFSET = 96;

/**
 * How long a click-pinned active id resists the position spy — comfortably
 * longer than a `scrollIntoView({ behavior: 'smooth' })` run over the guide's
 * longest jump (Chromium's smooth-scroll duration does not scale unbounded
 * with distance).
 */
const PIN_SETTLE_MS = 1000;

/** Nearest scrollable ancestor — the shell's `<main>` in practice. */
function findScrollParent(node: HTMLElement | null): HTMLElement | null {
  let element = node?.parentElement ?? null;
  while (element) {
    const overflowY = window.getComputedStyle(element).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return element;
    }
    element = element.parentElement;
  }
  return null;
}

/**
 * The topic currently under the reading line (or `null` while still on the
 * lead), plus `pin` — call it with a topic id right before scrolling to it so
 * that topic reads as active immediately, instead of waiting on (and
 * potentially losing to, see the file header) the position spy.
 */
function useActiveSection(
  ids: readonly string[],
  anchorRef: RefObject<HTMLElement | null>,
): readonly [string | null, (id: string) => void] {
  const [activeId, setActiveId] = useState<string | null>(null);
  const pinnedUntilRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const container = findScrollParent(anchorRef.current);
    const target: EventTarget = container ?? window;
    let frame = 0;

    const compute = (): void => {
      frame = 0;
      if (Date.now() < pinnedUntilRef.current) {
        return; // a click just pinned the active id — let the scroll settle first
      }
      const containerTop = container ? container.getBoundingClientRect().top : 0;
      const line = containerTop + ACTIVE_LINE_OFFSET;
      let next: string | null = null;
      for (const id of ids) {
        const element = document.getElementById(helpSectionElementId(id));
        if (!element) {
          continue;
        }
        if (element.getBoundingClientRect().top > line) {
          break; // sections are in DOM order — everything after is lower still
        }
        next = id;
      }
      // At the very bottom of the scroll a short trailing section can never
      // reach the line; snap to it so the last topic is reachable in the TOC.
      if (container && container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
        next = ids[ids.length - 1] ?? next;
      }
      setActiveId(next);
    };

    const onScroll = (): void => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(compute);
      }
    };

    compute();
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ids, anchorRef]);

  const pin = (id: string): void => {
    pinnedUntilRef.current = Date.now() + PIN_SETTLE_MS;
    setActiveId(id);
  };

  return [activeId, pin] as const;
}

export interface HelpTocProps {
  /** Which routed guide this TOC belongs to — scopes the spy ids + back-to-top target. */
  readonly page: HelpPageId;
  /** Topics to list — already filtered by the search query. */
  readonly topics: readonly HelpSectionMeta[];
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
}

export function HelpToc({ page, topics, query, onQueryChange }: HelpTocProps): React.JSX.Element {
  const navRef = useRef<HTMLElement | null>(null);
  const topicIds = useMemo(() => topicsForPage(page).map((topic) => topic.id), [page]);
  const [activeId, pinActive] = useActiveSection(topicIds, navRef);
  const leadId = HELP_LEAD_ID_BY_PAGE[page];

  const groups = HELP_GROUPS.map((group) => ({
    group,
    items: topics.filter((topic) => topic.group === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <nav
      ref={navRef}
      id="help-toc"
      data-testid="help-toc"
      aria-label="Help topics"
      className="flex max-h-[calc(100vh-7rem)] flex-col gap-3 overflow-y-auto rounded-lg border border-border bg-surface p-4 lg:sticky lg:top-0"
    >
      <div className="space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          Contents
        </p>
        <HelpSearch value={query} onChange={onQueryChange} resultCount={topics.length} />
      </div>

      {groups.length === 0 ? (
        <p
          data-testid="help-toc-empty"
          className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-text-muted"
        >
          No matching sections
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map(({ group, items }) => (
            <div key={group}>
              <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-text-muted">
                {group}
              </p>
              <ul className="space-y-0.5">
                {items.map((topic) => {
                  const active = topic.id === activeId;
                  return (
                    <li key={topic.id}>
                      <button
                        type="button"
                        data-testid={`help-toc-item-${topic.id}`}
                        data-number={topic.number}
                        data-active={active ? 'true' : 'false'}
                        aria-current={active ? 'true' : undefined}
                        onClick={() => {
                          pinActive(topic.id);
                          void scrollToHelpSection(topic.id).then(() => {
                            // Re-pin now that the scroll has actually been issued (past the
                            // image-settle wait, see scrollToHelpSection) — restarts the
                            // settle countdown from when the smooth scroll really starts,
                            // rather than from the click, whose timing it does not share.
                            pinActive(topic.id);
                          });
                        }}
                        className={`flex w-full items-baseline gap-2.5 rounded-r border-l-2 py-1.5 pl-2.5 pr-2 text-left text-sm transition ${
                          active
                            ? 'border-accent bg-accent/10 font-medium text-text-strong'
                            : 'border-transparent text-text-muted hover:bg-surface-muted hover:text-text-strong'
                        }`}
                      >
                        <span
                          aria-hidden
                          className={`w-4 shrink-0 text-right text-xs font-semibold tabular-nums ${
                            active ? 'text-accent' : 'text-text-muted/70'
                          }`}
                        >
                          {topic.number}
                        </span>
                        <span className="min-w-0">{topic.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Not a topic (so the TOC still lists exactly 13) — the way back to the
          top of the guide once you have scrolled deep into it. */}
      <button
        type="button"
        data-testid="help-back-to-top"
        onClick={() => void scrollToHelpSection(leadId)}
        className="mt-auto flex items-center justify-center gap-2 rounded-md border border-border py-2 text-xs font-medium text-text-muted transition hover:bg-surface-muted hover:text-text-strong"
      >
        <i className="fa-solid fa-arrow-up text-[10px]" aria-hidden />
        Back to top
      </button>
    </nav>
  );
}
